/**
 * GEMM - Coleções em sequência
 *
 * O Scroll Bee carrega as páginas da coleção aberta. Quando ele termina,
 * este componente continua o scroll com as outras coleções definidas em
 * "Configurações do tema > Coleções em sequência", da mais recente para a
 * mais antiga.
 *
 * Os produtos são buscados pela Section Rendering API
 * (sections/gemm-collection-chain.liquid) e inseridos no mesmo <product-list>
 * da coleção, então o grid e o seletor de layout continuam funcionando.
 *
 * O elemento <gemm-collection-chain> é renderizado logo após o grid em
 * sections/main-collection.liquid e também serve de marcador do fim do grid.
 *
 * Conforme o cliente rola, a quantidade de produtos na barra de ferramentas
 * acompanha a coleção que está passando sob ela.
 */
(() => {
  'use strict';

  if (window.customElements.get('gemm-collection-chain')) {
    return;
  }

  const SECTION_ID = 'gemm-collection-chain';

  // Distância (px) antes do fim do grid em que a próxima página começa a ser buscada.
  const PRELOAD_DISTANCE = 1200;

  // Falhas de rede seguidas antes de desistir.
  const MAX_FAILURES = 3;

  class GemmCollectionChain extends HTMLElement {
    connectedCallback() {
      this.productList = document.getElementById(this.getAttribute('product-list'));

      if (!this.productList) {
        return;
      }

      this.config = JSON.parse(this.querySelector('script[type="application/json"]').textContent);

      // URLs das outras coleções, na ordem da lista: da mais recente para a mais antiga (ver sections/main-collection.liquid).
      this.queue = [...this.config.collections];
      this.cursor = null;
      this.headings = [];
      this.started = false;
      this.finished = this.queue.length === 0;
      this.loading = false;
      this.failures = 0;
      this.abortController = new AbortController();

      this.maybeLoad = this.maybeLoad.bind(this);

      // O Scroll Bee dispara o evento antes de terminar de atualizar o próprio estado.
      this.onScrollBeeLoaded = () => setTimeout(this.maybeLoad, 100);
      document.addEventListener('scrollbee:loaded', this.onScrollBeeLoaded);

      this.intersectionObserver = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting) {
          this.maybeLoad();
        }
      }, { rootMargin: `0px 0px ${PRELOAD_DISTANCE}px 0px` });

      this.intersectionObserver.observe(this);

      this.productsCountElement = this.closest('.shopify-section')?.querySelector('.collection-toolbar__products-count');

      if (this.productsCountElement) {
        this.originalProductsCount = this.productsCountElement.textContent.trim();
        this.shownProductsCount = this.originalProductsCount;

        this.onScroll = () => {
          this.scrollFrame ??= requestAnimationFrame(() => {
            this.scrollFrame = null;
            this.updateProductsCount();
          });
        };

        window.addEventListener('scroll', this.onScroll, { passive: true });
      }
    }

    disconnectedCallback() {
      this.intersectionObserver?.disconnect();
      this.abortController?.abort();
      document.removeEventListener('scrollbee:loaded', this.onScrollBeeLoaded);
      window.removeEventListener('scroll', this.onScroll);
      cancelAnimationFrame(this.scrollFrame);
    }

    /**
     * Mostra na barra de ferramentas a quantidade de produtos da coleção cujo
     * cabeçalho já passou por baixo dela.
     */
    updateProductsCount() {
      const toolbarBottom = this.productsCountElement.closest('.collection-toolbar')?.getBoundingClientRect().bottom ?? 0;
      let productsCount = this.originalProductsCount;

      for (const heading of this.headings) {
        if (heading.getBoundingClientRect().top > toolbarBottom) {
          break;
        }

        productsCount = heading.dataset.productsCount;
      }

      if (productsCount !== this.shownProductsCount) {
        this.productsCountElement.textContent = productsCount;
        this.shownProductsCount = productsCount;
      }
    }

    /**
     * A coleção aberta terminou quando já estamos na última página, quando o
     * Scroll Bee já carregou a última página ou quando todos os produtos dela
     * já estão no grid.
     */
    isCurrentCollectionLoaded() {
      const { currentPage, totalPages, productsCount } = this.config;

      if (currentPage >= totalPages) {
        return true;
      }

      const scrollBee = window.ScrollBee;

      if (scrollBee && !scrollBee.isLoading && (scrollBee.lastLoadedPage || currentPage) >= totalPages) {
        return true;
      }

      return this.productList.querySelectorAll(':scope > product-card').length >= productsCount;
    }

    isNearViewport() {
      return this.getBoundingClientRect().top < window.innerHeight + PRELOAD_DISTANCE;
    }

    maybeLoad() {
      if (this.loading || this.finished || !this.isConnected) {
        return;
      }

      if (!this.started) {
        if (!this.isCurrentCollectionLoaded()) {
          return;
        }

        this.started = true;
      }

      if (this.isNearViewport()) {
        this.loadNextPage();
      }
    }

    async loadNextPage() {
      this.loading = true;
      this.setAttribute('loading', '');

      let failed = false;

      try {
        let appended = 0;

        // Pula páginas sem produtos novos (coleção vazia, indisponível ou só com produtos repetidos).
        while (appended === 0 && !this.finished) {
          if (!this.cursor || this.cursor.page > this.cursor.totalPages) {
            const url = this.queue.shift();

            if (!url) {
              this.finish();
              break;
            }

            this.cursor = { url, page: 1, totalPages: 1, heading: null };
          }

          const result = await this.fetchPage(this.cursor.url, this.cursor.page);

          this.cursor.page += 1;
          this.cursor.totalPages = result.totalPages;

          if (result.heading) {
            this.cursor.heading = result.heading;
          }

          appended = this.append(result.cards);
        }

        this.failures = 0;
      } catch (error) {
        if (error.name === 'AbortError') {
          return;
        }

        console.error('[GEMM] Erro ao carregar a próxima coleção:', error);
        failed = true;

        if (++this.failures >= MAX_FAILURES) {
          this.finish();
        }
      } finally {
        this.loading = false;
        this.removeAttribute('loading');
      }

      // Se o fim do grid continua perto da tela, segue carregando.
      if (failed) {
        setTimeout(this.maybeLoad, 3000 * this.failures);
      } else {
        requestAnimationFrame(this.maybeLoad);
      }
    }

    async fetchPage(collectionUrl, page) {
      const url = new URL(collectionUrl, window.location.origin);
      url.searchParams.set('section_id', SECTION_ID);
      url.searchParams.set('page', page);

      const response = await fetch(url, { signal: this.abortController.signal });

      // Coleção removida ou indisponível: segue para a próxima.
      if (response.status === 404) {
        return { cards: [], heading: null, totalPages: 0 };
      }

      if (!response.ok) {
        throw new Error(`HTTP ${response.status} em ${url.pathname}`);
      }

      const html = await response.text();
      const pageElement = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-gemm-chain-page]');

      if (!pageElement) {
        throw new Error(`Seção "${SECTION_ID}" não encontrada no tema.`);
      }

      return {
        cards: Array.from(pageElement.querySelectorAll(':scope > product-card')),
        heading: pageElement.querySelector(':scope > .gemm-collection-chain__heading'),
        totalPages: Number(pageElement.dataset.totalPages) || 0
      };
    }

    /**
     * Insere os cards no grid (com o cabeçalho da coleção antes do primeiro card
     * inserido dela) e devolve quantos foram inseridos.
     */
    append(cards) {
      if (this.config.hideDuplicates) {
        const shownHandles = new Set(
          Array.from(this.productList.querySelectorAll(':scope > product-card[handle]'), (card) => card.getAttribute('handle'))
        );

        cards = cards.filter((card) => !shownHandles.has(card.getAttribute('handle')));
      }

      if (cards.length === 0) {
        return 0;
      }

      const nodes = cards.map((card) => document.importNode(card, true));

      if (this.cursor.heading) {
        const heading = document.importNode(this.cursor.heading, true);

        this.headings.push(heading);
        nodes.unshift(heading);
        this.cursor.heading = null;
      }

      this.productList.append(...nodes);

      // O app de segunda imagem no hover (snippets/sioh-config.liquid) e outras
      // integrações já escutam este evento do Scroll Bee para processar produtos novos.
      document.dispatchEvent(new CustomEvent('scrollbee:loaded'));

      return cards.length;
    }

    finish() {
      this.finished = true;
      this.intersectionObserver.disconnect();
    }
  }

  window.customElements.define('gemm-collection-chain', GemmCollectionChain);
})();
